import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  SquaresFour, Plus, UsersThree, GearSix, SignOut, Vault, CaretRight, TrayArrowDown,
} from "@phosphor-icons/react";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";

const nav = [
  { to: "/admin", icon: SquaresFour, label: "Pipeline", end: true },
  { to: "/admin/applications", icon: TrayArrowDown, label: "Applications" },
  { to: "/admin/deals/new", icon: Plus, label: "New Deal" },
  { to: "/admin/team", icon: UsersThree, label: "Team", ownerOnly: true },
  { to: "/admin/settings", icon: GearSix, label: "Settings" },
];

export default function AdminLayout() {
  const { user, logout, isOwner } = useAuth();
  const navigate = useNavigate();
  const [brand, setBrand] = useState({ company_name: "Business Line OC" });
  const [newApps, setNewApps] = useState(0);

  useEffect(() => {
    api.get("/settings").then((r) => setBrand((p) => ({ ...p, ...r.data, company_name: r.data.company_name || p.company_name }))).catch(() => {});
    api.get("/applications/stats").then((r) => setNewApps(r.data.new_count || 0)).catch(() => {});
  }, []);

  const logoUrl = brand.logo_path ? `${api.defaults.baseURL}/branding/logo?v=${brand.logo_path}` : null;

  return (
    <div className="theme-admin min-h-screen bg-background text-foreground flex">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 shrink-0 flex-col border-r border-border bg-card/40 relative">
        <div className="absolute inset-0 dot-grid opacity-40 pointer-events-none" aria-hidden />
        <div className="relative p-5 flex items-center gap-2.5 border-b border-border">
          {logoUrl ? (
            <img src={logoUrl} alt="logo" className="h-9 w-9 rounded-lg object-cover" />
          ) : (
            <div className="h-9 w-9 rounded-lg bg-primary/15 border border-primary/30 flex items-center justify-center">
              <Vault size={20} weight="duotone" className="text-primary" />
            </div>
          )}
          <div className="min-w-0">
            <div className="font-display font-bold text-sm truncate">{brand.company_name}</div>
            <div className="overline text-muted-foreground mt-0.5">Console</div>
          </div>
        </div>

        <nav className="relative flex-1 p-3 space-y-1">
          {nav.filter((n) => !n.ownerOnly || isOwner).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.label.toLowerCase().replace(" ", "-")}`}
              className={({ isActive }) =>
                `group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary/12 text-primary border border-primary/25"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary border border-transparent"
                }`
              }
            >
              <n.icon size={19} weight="duotone" />
              {n.label}
              {n.label === "Applications" && newApps > 0 && (
                <span className="ml-auto num text-[11px] font-semibold bg-primary text-primary-foreground rounded-full px-1.5 min-w-[18px] h-[18px] flex items-center justify-center" data-testid="apps-badge">{newApps}</span>
              )}
              <CaretRight size={14} className={`${n.label === "Applications" && newApps > 0 ? "hidden" : "ml-auto"} opacity-0 group-hover:opacity-40 transition-opacity`} />
            </NavLink>
          ))}
        </nav>

        <div className="relative p-3 border-t border-border">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="h-9 w-9 rounded-full bg-secondary flex items-center justify-center text-sm font-semibold">
              {user?.name?.[0] || "U"}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-xs text-muted-foreground capitalize">{user?.role}</div>
            </div>
            <button
              data-testid="logout-button"
              onClick={() => { logout(); navigate("/login"); }}
              className="text-muted-foreground hover:text-destructive transition-colors"
              title="Sign out"
            >
              <SignOut size={18} />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-30 glass border-b border-border flex items-center justify-between px-4 h-14">
        <div className="flex items-center gap-2">
          <Vault size={20} weight="duotone" className="text-primary" />
          <span className="font-display font-bold text-sm">{brand.company_name}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={() => navigate("/admin/deals/new")} data-testid="mobile-new-deal">
            <Plus size={18} />
          </Button>
          <button onClick={() => { logout(); navigate("/login"); }} className="text-muted-foreground">
            <SignOut size={18} />
          </button>
        </div>
      </div>

      <main className="flex-1 min-w-0 md:pt-0 pt-14">
        <Outlet />
      </main>
    </div>
  );
}
